import { makeAutoObservable, runInAction } from 'mobx';
import { RootStore } from '../../RootStore';
import { hospitalService } from '../../../services/modules/hospital/hospital.service';
import { IHospitalResult, IHospitalParam } from '../../../models/hospital/HospitalModel';
import { GridApi } from 'ag-grid-community';
import { toast } from 'react-toastify';

interface ODataParams {
  $filter?: string;
  $orderby?: string;
  $skip?: number;
  $top?: number;
  $select?: string;
  $count?: boolean;
}

interface GridParams {
  startRow: number;
  endRow: number;
  filterModel: any;
  sortModel: any;
}

export class HospitalStore {
  rootStore: RootStore;
  hospitalList: IHospitalResult[] = [];
  gridApi: GridApi<IHospitalResult> | null = null;
  selectedHospitalToDelete: IHospitalResult | null = null;
  selectedHospitalToEdit: IHospitalResult | null = null;
  selectedHospitalForTags: IHospitalResult | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  setGridApi = (api: GridApi<IHospitalResult>) => {
    this.gridApi = api;
  };

  refreshGrid = () => {
    if (this.gridApi) {
      this.gridApi.refreshServerSide();
      this.gridApi.refreshInfiniteCache();
    }
  };

  refreshGridWithCurrentSettings = () => {
    if (this.gridApi) {
      // Grid'in mevcut filtreleme, sıralama ve sayfalama ayarlarını koruyarak refresh yap
      this.gridApi.refreshServerSide();
      this.gridApi.refreshInfiniteCache();
    }
  };

  listAllHospitals = async (
    params?: GridParams
  ): Promise<{ rows: IHospitalResult[]; lastRow: number; count: number }> => {
    try {
      this.rootStore.uiStore.setLoading(true);

      if (params) {
        const odataParams: ODataParams = {
          $count: true,
          $skip: params.startRow,
          $top: params.endRow - params.startRow,
        };

        if (params.filterModel) {
          const filters = Object.entries(params.filterModel).map(
            ([field, filter]: [string, any]) => {
              const numericFields = ['id'];
              if (numericFields.includes(field)) {
                return `${field} eq ${filter.filter}`;
              }
              return `contains(${field}, '${filter.filter}')`;
            }
          );

          if (filters.length > 0) {
            odataParams.$filter = filters.join(' and ');
          }
        }

        if (params.sortModel && params.sortModel.length > 0) {
          const { colId, sort } = params.sortModel[0];
          odataParams.$orderby = `${colId} ${sort === 'asc' ? 'asc' : 'desc'}`;
        }

        const response: any = await hospitalService.listAllHospitalsWithOData({
          params: odataParams,
        });

        return runInAction(() => {
          if (response) {
            const totalCount = response['@odata.count'] || 0;
            const hospitals = response.value || [];
            this.hospitalList = hospitals;
            return {
              rows: hospitals,
              lastRow: totalCount,
              count: totalCount,
            };
          }
          return {
            rows: [],
            lastRow: 0,
            count: 0,
          };
        });
      } else {
        const response: any = await hospitalService.listAllHospitals();

        runInAction(() => {
          this.hospitalList = Array.isArray(response) ? response : [];
        });

        return {
          rows: this.hospitalList,
          lastRow: this.hospitalList.length,
          count: this.hospitalList.length,
        };
      }
    } catch (error) {
      console.error('Error fetching hospitals:', error);
      runInAction(() => {
        this.rootStore.uiStore.setError(
          error instanceof Error ? error.message : 'An unknown error occurred'
        );
      });
      throw error;
    } finally {
      runInAction(() => {
        this.rootStore.uiStore.setLoading(false);
      });
    }
  };

  newHospital = async (values: IHospitalParam) => {
    try {
      this.rootStore.uiStore.setLoading(true);
      const response = await hospitalService.newHospital(values);

      if (response.error != true) {
        // Grid'i mevcut ayarlarla refresh et
        this.refreshGridWithCurrentSettings();
      }
      return response;
    } catch (error) {
      console.error('Error creating hospital:', error);
      runInAction(() => {
        this.rootStore.uiStore.setError(
          error instanceof Error ? error.message : 'An unknown error occurred'
        );
        toast.error('Hastane oluşturulurken bir hata oluştu');
      });
      throw error;
    } finally {
      runInAction(() => {
        this.rootStore.uiStore.setLoading(false);
      });
    }
  };

  setSelectedHospitalToDelete = (hospital: IHospitalResult | null) => {
    this.selectedHospitalToDelete = hospital;
  };

  setSelectedHospitalToEdit = (hospital: IHospitalResult | null) => {
    this.selectedHospitalToEdit = hospital;
  };

  setSelectedHospitalForTags = (hospital: IHospitalResult | null) => {
    this.selectedHospitalForTags = hospital;
  };

  updateHospital = async (id: number, values: IHospitalParam) => {
    try {
      this.rootStore.uiStore.setLoading(true);
      const response = await hospitalService.updateHospital(id, values);

      if (response.error === false) {
        this.refreshGridWithCurrentSettings();
      } else {
        toast.error('Hastane güncellenemedi. ' + response.message);
      }
      return response;
    } catch (error) {
      console.error('Error updating hospital:', error);
      toast.error('Hastane güncellenirken bir hata oluştu');
      throw error;
    } finally {
      this.rootStore.uiStore.setLoading(false);
    }
  };

  deleteHospital = async (id: number) => {
    try {
      this.rootStore.uiStore.setLoading(true);
      const response = await hospitalService.delete(id);
      if (response.error == false) {
        toast.success('Hastane başarıyla silindi.');
        // Grid'i mevcut ayarlarla refresh et
        this.refreshGridWithCurrentSettings();
      } else {
        toast.error('Hastane silinemedi. ' + response.message);
      }
      return response;
    } catch (error) {
      console.error('Hastane silinirken hata:', error);
      toast.error('Hastane silinirken bir hata oluştu');
      throw error;
    } finally {
      this.rootStore.uiStore.setLoading(false);
    }
  };
}
